import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { UserDropdownDto } from '../../types';
import { usersApi } from '../../api/users';

interface MultiEmployeeTypeaheadProps {
  value: UserDropdownDto[];
  onChange: (users: UserDropdownDto[]) => void;
  excludeIds?: string[];
  label: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /** Limit search results to active members of this BRG. */
  brgId?: number;
  /** Maximum number of users. Once reached, no more can be added and maxReachedMessage is shown. */
  maxSelections?: number;
  maxReachedMessage?: string;
  /** Leave already-selected/excluded users out of the results instead of listing them disabled. */
  hideSelected?: boolean;
  placeholder?: string;
  labelStyle?: React.CSSProperties;
}

export function MultiEmployeeTypeahead({
  value,
  onChange,
  excludeIds = [],
  label,
  required = false,
  error,
  disabled = false,
  brgId,
  maxSelections,
  maxReachedMessage,
  hideSelected = false,
  placeholder,
  labelStyle,
}: MultiEmployeeTypeaheadProps) {
  const inputId = useId();
  const atMax = maxSelections !== undefined && value.length >= maxSelections;
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState<UserDropdownDto[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Get all IDs to exclude (externally excluded + already selected)
  const allExcludeIds = [
    ...excludeIds,
    ...value.map(u => u.userId.toString()),
  ];

  const fetchUsers = useCallback(async (query: string) => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const data = await usersApi.search(query, true, brgId);
      setResults(data);
      setIsOpen(true);
    } catch {
      setFetchError('Failed to load users');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [brgId]);

  // Results shown in the dropdown (max 10), optionally without already-selected users
  const visibleResults = (hideSelected
    ? results.filter(u => !allExcludeIds.includes(u.userId.toString()))
    : results
  ).slice(0, 10);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (atMax) return;
    const text = e.target.value;
    setInputValue(text);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fetchUsers(text);
    }, 300);
  }

  function handleSelect(user: UserDropdownDto) {
    const isExcluded = allExcludeIds.includes(user.userId.toString());
    if (isExcluded || atMax) return;

    // Cancel a search still pending from typing so it doesn't reopen the list after selection
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onChange([...value, user]);
    setInputValue('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  }

  function handleRemove(userId: number) {
    onChange(value.filter(u => u.userId !== userId));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      // Remove last chip on backspace when input is empty
      handleRemove(value[value.length - 1].userId);
    }
  }

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    marginBottom: '0',
  };

  const inputContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    border: `1px solid ${error ? '#d32f2f' : '#d0d0d0'}`,
    borderRadius: '4px',
    backgroundColor: disabled ? '#f9f9f9' : '#fff',
    minHeight: '44px',
    cursor: disabled ? 'not-allowed' : 'text',
  };

  const chipStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: '#e3f0fb',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#1a365d',
    maxWidth: '200px',
  };

  const chipTextStyle: React.CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const removeButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    border: 'none',
    background: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: 0,
    color: '#4a5568',
    fontSize: '14px',
    lineHeight: 1,
    borderRadius: '50%',
    transition: 'background-color 0.15s',
  };

  const inputStyle: React.CSSProperties = {
    flex: 1,
    minWidth: '120px',
    border: 'none',
    outline: 'none',
    padding: '4px 0',
    fontSize: '0.95rem',
    backgroundColor: 'transparent',
    cursor: disabled ? 'not-allowed' : 'text',
  };

  return (
    <div ref={containerRef} style={containerStyle}>
      <label htmlFor={inputId} style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.85rem', color: '#000000', ...labelStyle }}>
        {label}
        {required && <span style={{ color: '#d32f2f', marginLeft: '2px' }}>*</span>}
      </label>
      
      <div 
        style={inputContainerStyle}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((user) => (
          <span key={user.userId} style={chipStyle}>
            <span style={chipTextStyle} title={user.displayName}>
              {user.displayName}
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(user.userId);
                }}
                style={removeButtonStyle}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#cbd5e0';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                }}
                aria-label={`Remove ${user.displayName}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (atMax) return;
            if (results.length > 0) {
              setIsOpen(true);
            } else {
              // Always fetch on focus, even with empty input
              void fetchUsers(inputValue);
            }
          }}
          onClick={() => {
            if (atMax) return;
            // Handle click when already focused - results may have been cleared after previous selection
            if (!isOpen && results.length === 0) {
              void fetchUsers(inputValue);
            }
          }}
          placeholder={
            atMax
              ? ''
              : value.length === 0
                ? (placeholder ?? `Search and select ${label.toLowerCase()}...`)
                : 'Add another...'
          }
          disabled={disabled}
          readOnly={atMax}
          style={inputStyle}
          autoComplete="off"
          aria-label={label}
          aria-expanded={isOpen && !atMax}
          aria-autocomplete="list"
          aria-describedby={atMax && maxReachedMessage ? `${inputId}-max` : undefined}
        />
      </div>

      {atMax && maxReachedMessage && (
        <p id={`${inputId}-max`} data-testid="max-selections-message" style={{ color: '#666', fontSize: '0.8rem', margin: '4px 0 0' }}>
          {maxReachedMessage}
        </p>
      )}
      {error && (
        <p style={{ color: '#d32f2f', fontSize: '0.8rem', margin: '4px 0 0', fontWeight: 500 }}>{error}</p>
      )}
      {fetchError && (
        <p style={{ color: '#d32f2f', fontSize: '0.8rem', margin: '4px 0 0' }}>{fetchError}</p>
      )}
      {isLoading && (
        <p style={{ color: '#666', fontSize: '0.8rem', margin: '4px 0 0' }}>Searching...</p>
      )}
      
      {isOpen && !atMax && visibleResults.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            border: '1px solid #d0d0d0',
            borderTop: 'none',
            borderRadius: '0 0 4px 4px',
            maxHeight: '260px',
            overflowY: 'auto',
            zIndex: 1000,
            listStyle: 'none',
            margin: 0,
            padding: 0,
            boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
          }}
        >
          {visibleResults.map((user) => {
            const isExcluded = allExcludeIds.includes(user.userId.toString());
            const isSelected = value.some(v => v.userId === user.userId);
            return (
              <li
                key={user.userId}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(user);
                }}
                style={{
                  padding: '10px 12px',
                  cursor: isExcluded ? 'not-allowed' : 'pointer',
                  opacity: isExcluded ? 0.5 : 1,
                  backgroundColor: isSelected ? '#e3f0fb' : undefined,
                  borderBottom: '1px solid #f0f0f0',
                }}
                onMouseEnter={(e) => {
                  if (!isExcluded) {
                    (e.currentTarget as HTMLLIElement).style.backgroundColor = '#f0f7ff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLLIElement).style.backgroundColor = '';
                  }
                }}
              >
                <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{user.displayName}</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>{user.email}</div>
                {isExcluded && isSelected && (
                  <div style={{ fontSize: '0.75rem', color: '#999' }}>Already selected</div>
                )}
                {isExcluded && !isSelected && (
                  <div style={{ fontSize: '0.75rem', color: '#999' }}>Already assigned to another role</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
